import React from 'react';
import { FD130, FD131 } from '../../../services/pwd212Api';
import { fmt } from '../helpers';

interface Tab4Props {
  formData: Partial<FD130>;
  details: Partial<FD131>[];
  selectedRow: number;
  isEditing: boolean;
  setSelectedRow: (idx: number) => void;
  getDetailField: (key: keyof FD131) => FD131[keyof FD131];
  setDetailField: (key: keyof FD131, value: FD131[keyof FD131]) => void;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
}

const Tab4ColorDetail: React.FC<Tab4Props> = React.memo(({
  formData, details, selectedRow, isEditing,
  setSelectedRow, getDetailField, setDetailField,
  addDetailRow, deleteDetailRow,
}) => {
  const disabled = !isEditing;
  const pricingMethod = formData.fd130_40 || 1;
  const sel = details[selectedRow] as Partial<FD131> | undefined;

  return (
    <>
      {/* Grid */}
      <div className="table-wrapper">
        <div className="table-toolbar">
          <div className="left">
            <span style={{ fontSize: 13 }}>色號明細 ({details.length} 項)</span>
            <span style={{ fontSize: 12, marginLeft: 12, color: 'var(--text-secondary)' }}>總項數: {formData.fd130_42 ?? 0}</span>
          </div>
          <div className="right">
            <button className="btn btn-sm" disabled={disabled || details.length >= 99} onClick={addDetailRow}>明細新增</button>
            <button className="btn btn-sm btn-danger" disabled={disabled || details.length === 0} onClick={deleteDetailRow}>明細刪除</button>
          </div>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>項次</th>
                <th>色號</th>
                <th className="num">成品量</th>
                <th className="num">指染量</th>
                <th className="num">工繳單價</th>
                <th className="num">一缸工繳</th>
                <th className="num">允收增加量+</th>
                <th className="num">允收比率+</th>
                <th className="num">允收增加量-</th>
                <th className="num">允收比率-</th>
                <th>CBNO</th>
              </tr>
            </thead>
            <tbody>
              {details.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>無明細</td></tr>
              ) : details.map((d, i) => (
                <tr key={i}
                  className={selectedRow === i ? 'selected' : ''}
                  onClick={() => setSelectedRow(i)}
                >
                  <td>{d.fd131_02}</td>
                  <td>{d.fd131_11 || ''}</td>
                  <td className="num">{fmt(d.fd131_13)}</td>
                  <td className="num">{fmt(d.fd131_14)}</td>
                  <td className="num">{fmt(d.fd131_16)}</td>
                  <td className="num">{fmt(d.fd131_26)}</td>
                  <td className="num">{fmt(d.fd131_242)}</td>
                  <td className="num">{fmt(d.fd131_241)}</td>
                  <td className="num">{fmt(d.fd131_244)}</td>
                  <td className="num">{fmt(d.fd131_243)}</td>
                  <td>{d.fd131_11a || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel (below grid) */}
      {sel && (
        <div className="detail-panel">
          <label>色號</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_11') as string}
            onChange={e => setDetailField('fd131_11', e.target.value)}
          />

          <label>色號名稱</label>
          <input type="text" style={{ maxWidth: 160 }}
            disabled={disabled}
            value={getDetailField('fd131_12') as string}
            onChange={e => setDetailField('fd131_12', e.target.value)}
          />

          <label>成品量</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.1"
            disabled={disabled}
            value={getDetailField('fd131_13') as number || ''}
            onChange={e => setDetailField('fd131_13', parseFloat(e.target.value) || 0)}
          />

          <label>指染量</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.1"
            disabled={disabled}
            value={getDetailField('fd131_14') as number || ''}
            onChange={e => setDetailField('fd131_14', parseFloat(e.target.value) || 0)}
          />

          <label>工繳單價</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.01"
            disabled={disabled || pricingMethod !== 1}
            value={getDetailField('fd131_16') as number || ''}
            onChange={e => setDetailField('fd131_16', parseFloat(e.target.value) || 0)}
          />

          <label>一缸工繳</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.01"
            disabled={disabled || pricingMethod !== 2}
            value={getDetailField('fd131_26') as number || ''}
            onChange={e => setDetailField('fd131_26', parseFloat(e.target.value) || 0)}
          />

          <label>允收量</label>
          <input type="number" readOnly style={{ maxWidth: 100 }}
            value={getDetailField('fd131_24') as number || ''}
          />

          <label>允收增加量+</label>
          <input type="number" style={{ maxWidth: 80 }} step="0.01"
            disabled={disabled}
            value={getDetailField('fd131_242') as number || ''}
            onChange={e => setDetailField('fd131_242', parseFloat(e.target.value) || 0)}
          />

          <label>允收比率+</label>
          <input type="number" style={{ maxWidth: 80 }} step="0.01"
            disabled={disabled}
            value={getDetailField('fd131_241') as number || ''}
            onChange={e => setDetailField('fd131_241', parseFloat(e.target.value) || 0)}
          />

          <label>允收增加量-</label>
          <input type="number" style={{ maxWidth: 80 }} step="0.01"
            disabled={disabled}
            value={getDetailField('fd131_244') as number || ''}
            onChange={e => setDetailField('fd131_244', parseFloat(e.target.value) || 0)}
          />

          <label>允收比率-</label>
          <input type="number" style={{ maxWidth: 80 }} step="0.01"
            disabled={disabled}
            value={getDetailField('fd131_243') as number || ''}
            onChange={e => setDetailField('fd131_243', parseFloat(e.target.value) || 0)}
          />

          <label>工繳小計</label>
          <input type="text" readOnly style={{ maxWidth: 120, textAlign: 'right' }}
            value={fmt(sel.fd131_25)}
          />

          <label>色卡號碼</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_20') as string}
            onChange={e => setDetailField('fd131_20', e.target.value)}
          />

          <label>打色通知單號</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_21') as string}
            onChange={e => setDetailField('fd131_21', e.target.value)}
          />

          <label>顏色類別</label>
          <select style={{ maxWidth: 160 }}
            disabled={disabled}
            value={getDetailField('fd131_22') as string || ''}
            onChange={e => setDetailField('fd131_22', e.target.value)}
          >
            <option value="">--</option>
            <option value="1">SWATCH</option>
            <option value="2">PANTON CARD</option>
          </select>

          <label>顏色號碼</label>
          <input type="text" style={{ maxWidth: 120 }} maxLength={15}
            disabled={disabled || (getDetailField('fd131_22') as string) === '1'}
            value={getDetailField('fd131_23') as string}
            onChange={e => setDetailField('fd131_23', e.target.value)}
          />

          <label>印花配色</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_19') as string}
            onChange={e => setDetailField('fd131_19', e.target.value)}
          />

          <label>CBNO</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_11a') as string}
            onChange={e => setDetailField('fd131_11a', e.target.value)}
          />

          <label>客戶實際需求量</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.1"
            disabled={disabled}
            value={getDetailField('fd131_27') as number || ''}
            onChange={e => setDetailField('fd131_27', parseFloat(e.target.value) || 0)}
          />
        </div>
      )}
    </>
  );
});

Tab4ColorDetail.displayName = 'Tab4ColorDetail';
export default Tab4ColorDetail;
